import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { StageTracker, PROJECT_STAGES } from "@/components/StageTracker";
import { PortalBillingTab } from "@/components/PortalBillingTab";
import { PortalMaterialsTab } from "@/components/PortalMaterialsTab";
import { PortalFormsTab } from "@/components/PortalFormsTab";
import { PortalDesignProposal } from "@/components/wizard/PortalDesignProposal";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Building2,
  Package,
  ClipboardCheck,
  Calendar,
  MessageSquare,
  FileText,
  Send,
  Plus,
  Clock,
  MapPin,
  User,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  FolderOpen,
  Download,
  File,
  Image,
  FileCheck,
  Receipt,
  WandSparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerPortalPage() {
  const { accessToken } = useParams();
  const reactToUpdate = async (updateId, reaction, message) => {
    try { const r = await axios.post(`${API}/portal/public/${accessToken}/updates/${updateId}/react`, { reaction, message }); toast.success(r.data.message); fetchPortalData?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  // Documents state
  const [documents, setDocuments] = useState({ documents: [], estimates: [], photos: [] });
  const [newRequest, setNewRequest] = useState({ title: "", description: "", category: "general" });
  const [submittingRequest, setSubmittingRequest] = useState(false);

  useEffect(() => {
    fetchPortalData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (activeTab === "messages") {
      fetchMessages();
    } else if (activeTab === "requests") {
      fetchRequests();
    } else if (activeTab === "documents") {
      fetchDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/portal/public/${accessToken}`);
      setPortalData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load portal. The link may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/portal/public/${accessToken}/documents`);
      setDocuments(response.data);
    } catch (err) {
      // Error handled silently — non-critical operation
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/portal/public/${accessToken}/messages`);
      setMessages(response.data);
    } catch (err) {
      // Error handled silently — non-critical operation
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/portal/public/${accessToken}/requests`);
      setRequests(response.data);
    } catch (err) {
      // Error handled silently — non-critical operation
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/portal/public/${accessToken}/messages`, {
        content: newMessage
      });
      setMessages([...messages, response.data]);
      setNewMessage("");
      toast.success("Message sent!");
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const submitRequest = async () => {
    if (!newRequest.title.trim() || !newRequest.description.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/portal/public/${accessToken}/requests`, newRequest);
      toast.success("Request submitted! Your project manager will review it.");
      setShowRequestModal(false);
      setNewRequest({ title: "", description: "", category: "general" });
      fetchRequests();
      fetchPortalData(); // Update pending count
    } catch (err) {
      toast.error("Failed to submit request");
    } finally {
      setSubmittingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { access, project, upcoming_schedules, customer_notes, project_manager, pending_requests, unread_messages } = portalData;

  const tabs = [
    { id: "overview", label: "Overview", icon: Building2 },
    ...(portalData.design_proposal ? [{ id: "design", label: "Design Proposal", icon: WandSparkles, badge: portalData.design_proposal.status === "proposal_ready" ? 1 : undefined }] : []),
    { id: "schedule", label: "Schedule", icon: Calendar, badge: upcoming_schedules?.length },
    { id: "documents", label: "Documents", icon: FolderOpen },
    { id: "billing", label: "Contracts & Billing", icon: FileText, badge: portalData.billing?.balance > 0 ? "$" : undefined },
    { id: "materials", label: "Materials", icon: Package, badge: portalData.procurement?.filter(p => p.status !== "received").length || undefined },
    { id: "forms", label: "Selections", icon: ClipboardCheck, badge: portalData.shared_forms?.filter(f => f.confirmation?.status !== "confirmed").length || undefined },
    { id: "messages", label: "Messages", icon: MessageSquare, badge: unread_messages },
    { id: "requests", label: "Requests", icon: FileText, badge: pending_requests },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "active": case "scheduled": return "bg-green-100 text-green-700";
      case "in_progress": return "bg-blue-100 text-blue-700";
      case "completed": return "bg-slate-100 text-slate-700";
      case "pending": return "bg-amber-100 text-amber-700";
      case "approved": return "bg-green-100 text-green-700";
      case "rejected": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{project.name}</h1>
              <p className="text-sm text-slate-500">Welcome, {access.customer_name}</p>
            </div>
            {project_manager && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <User className="w-4 h-4" />
                <span>PM: {project_manager.name}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto" data-testid="portal-navigation-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`portal-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] flex items-center justify-center">
                    {tab.badge}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {activeTab === "design" && portalData.design_proposal && (
          <PortalDesignProposal api={API} accessToken={accessToken} proposal={portalData.design_proposal} onAccepted={fetchPortalData} />
        )}
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6 animate-fade-in">
            {/* Live progress tracker */}
            <Card data-testid="portal-tracker-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-indigo-600" /> Where your project is right now</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <StageTracker stage={project.stage} completed={project.status === "completed"} />
                {project.status === "completed" ? (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">Your project is complete. Thank you for choosing us!</p>
                ) : (
                  <p className="text-sm text-slate-600">Currently in <b>{PROJECT_STAGES.find(s => s.key === (project.stage || "design"))?.label}</b>{project.stage_updated_at && <> since {new Date(project.stage_updated_at).toLocaleDateString()}</>}. Everything is on track — we'll post updates here as things progress.</p>
                )}
                {portalData.updates?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Latest updates</p>
                    <div className="space-y-3">
                      {portalData.updates.slice(0, 6).map(u => (
                        <div key={u.update_id} className="flex gap-3 text-sm" data-testid={`portal-update-${u.update_id}`}>
                          <div className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
                          <div><p className="text-slate-800">{u.note}</p>
                            {u.photo_ids?.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{u.photo_ids.map(id => <a key={id} href={`${API}/portal/public/${accessToken}/photo/${id}`} target="_blank" rel="noreferrer"><img src={`${API}/portal/public/${accessToken}/photo/${id}`} alt="Progress" className="w-28 h-28 object-cover rounded-lg border hover:opacity-90" data-testid={`portal-photo-${id}`} /></a>)}</div>}
                            <div className="flex items-center gap-2 mt-1">
                              <button type="button" onClick={() => reactToUpdate(u.update_id, "thumbs_up")} className="text-xs px-2 py-0.5 rounded-full border border-slate-200 hover:bg-indigo-50" data-testid={`react-up-${u.update_id}`}>👍 Looks great{u.reactions?.filter(r => r.reaction === "thumbs_up").length ? ` (${u.reactions.filter(r => r.reaction === "thumbs_up").length})` : ""}</button>
                              <button type="button" onClick={() => { const q = window.prompt("What would you like to ask about this update?"); if (q !== null) reactToUpdate(u.update_id, "question", q); }} className="text-xs px-2 py-0.5 rounded-full border border-slate-200 hover:bg-amber-50" data-testid={`react-q-${u.update_id}`}>❓ Ask a question</button>
                            </div>
                            <p className="text-xs text-slate-400">{u.stage_label} · {new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Project Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status?.replace("_", " ").toUpperCase()}
                    </Badge>
                  </div>
                  {project.address && (
                    <div>
                      <p className="text-sm text-slate-500">Location</p>
                      <p className="font-medium flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {project.address}
                      </p>
                    </div>
                  )}
                  {project.description && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-slate-500">Description</p>
                      <p className="text-slate-700">{project.description}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Customer Notes - Important Instructions */}
            {customer_notes?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    Important Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {customer_notes.map(note => (
                      <li key={note.note_id} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span className="text-amber-900">{note.content}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("messages")}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <MessageSquare className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">Message Your PM</p>
                        <p className="text-sm text-slate-500">Chat directly with your project manager</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowRequestModal(true)}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Plus className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Submit a Request</p>
                        <p className="text-sm text-slate-500">Special instructions or preferences</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Schedule Preview */}
            {upcoming_schedules?.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Upcoming Work
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("schedule")}>
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {upcoming_schedules.slice(0, 3).map(sched => (
                      <div key={sched.schedule_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <div className="text-center min-w-[60px]">
                          <p className="text-xs text-slate-500">
                            {new Date(sched.start_time).toLocaleDateString("en-US", { month: "short" })}
                          </p>
                          <p className="text-2xl font-bold text-indigo-600">
                            {new Date(sched.start_time).getDate()}
                          </p>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{sched.title}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(sched.start_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                            {sched.assigned_names?.length > 0 && ` • ${sched.assigned_names.join(", ")}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === "schedule" && (
          <div className="space-y-4 animate-fade-in">
            <h2 className="text-lg font-semibold text-slate-900">Upcoming Schedule</h2>
            {upcoming_schedules?.length > 0 ? (
              <div className="space-y-3">
                {upcoming_schedules.map(sched => (
                  <Card key={sched.schedule_id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-4">
                        <div className="text-center min-w-[70px] p-2 bg-indigo-50 rounded-lg">
                          <p className="text-xs text-indigo-600 font-medium">
                            {new Date(sched.start_time).toLocaleDateString("en-US", { weekday: "short" })}
                          </p>
                          <p className="text-2xl font-bold text-indigo-600">
                            {new Date(sched.start_time).getDate()}
                          </p>
                          <p className="text-xs text-indigo-500">
                            {new Date(sched.start_time).toLocaleDateString("en-US", { month: "short" })}
                          </p>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{sched.title}</h3>
                          {sched.description && (
                            <p className="text-sm text-slate-600 mt-1">{sched.description}</p>
                          )}
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(sched.start_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                              {" - "}
                              {new Date(sched.end_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                            </span>
                            {sched.assigned_names?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {sched.assigned_names.join(", ")}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge className={getStatusColor(sched.status)}>
                          {sched.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No upcoming work scheduled</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div className="animate-fade-in">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages with {project_manager?.name || "Project Manager"}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length > 0 ? (
                  messages.map(msg => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_type === "customer" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.sender_type === "customer"
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-100 text-slate-900"
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === "customer" ? "text-indigo-200" : "text-slate-400"}`}>
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No messages yet. Start a conversation!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </CardContent>
              <div className="border-t p-4">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    disabled={sendingMessage}
                  />
                  <Button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()}>
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === "requests" && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Your Requests</h2>
              <Button onClick={() => setShowRequestModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </div>
            
            {requests.length > 0 ? (
              <div className="space-y-3">
                {requests.map(req => (
                  <Card key={req.request_id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{req.title}</h3>
                            <Badge className={getStatusColor(req.status)}>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{req.description}</p>
                          <p className="text-xs text-slate-400 mt-2">
                            Submitted {new Date(req.created_at).toLocaleDateString()}
                          </p>
                          {req.status === "rejected" && req.rejection_reason && (
                            <p className="text-sm text-red-600 mt-2">
                              Reason: {req.rejection_reason}
                            </p>
                          )}
                        </div>
                        {req.status === "approved" && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No requests submitted yet</p>
                  <Button onClick={() => setShowRequestModal(true)}>
                    Submit Your First Request
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "billing" && <PortalBillingTab billing={portalData.billing} />}
        {activeTab === "materials" && <PortalMaterialsTab procurement={portalData.procurement} />}
        {activeTab === "forms" && <PortalFormsTab accessToken={accessToken} forms={portalData.shared_forms} onChanged={fetchPortalData} />}

        {activeTab === "documents" && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-slate-900">Document Center</h2>
            
            {/* Contracts & Invoices */}
            {documents.documents?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <File className="w-5 h-5" />
                    Contracts & Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {documents.documents.map(doc => (
                      <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          {doc.file_type === "contract" && <FileCheck className="w-5 h-5 text-indigo-500" />}
                          {doc.file_type === "invoice" && <Receipt className="w-5 h-5 text-green-500" />}
                          {doc.file_type === "photo" && <Image className="w-5 h-5 text-blue-500" />}
                          {!["contract", "invoice", "photo"].includes(doc.file_type) && <File className="w-5 h-5 text-slate-500" />}
                          <div>
                            <p className="font-medium text-slate-900">{doc.name}</p>
                            <p className="text-xs text-slate-500">
                              {doc.file_type.charAt(0).toUpperCase() + doc.file_type.slice(1)} • 
                              {(doc.file_size / 1024).toFixed(1)} KB • 
                              {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <a
                          href={`${API}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Estimates */}
            {documents.estimates?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="w-5 h-5" />
                    Estimates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {documents.estimates.map(est => (
                      <div key={est.estimate_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="font-medium text-slate-900">Estimate #{est.estimate_number}</p>
                            <p className="text-xs text-slate-500">
                              ${est.total?.toLocaleString()} • Accepted {new Date(est.accepted_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <a
                          href={`${API}/estimates/${est.estimate_id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Photos */}
            {documents.photos?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Image className="w-5 h-5" />
                    Project Photos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {documents.photos.map(photo => (
                      <a
                        key={photo.photo_id}
                        href={photo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100"
                      >
                        <img
                          src={photo.url}
                          alt={photo.caption || "Project photo"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <Download className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                            <p className="text-xs text-white truncate">{photo.caption}</p>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {!documents.documents?.length && !documents.estimates?.length && !documents.photos?.length && (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No documents available yet</p>
                  <p className="text-sm text-slate-400 mt-1">Documents will appear here as your project progresses</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* New Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit a Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category</Label>
              <Select
                value={newRequest.category}
                onValueChange={(v) => setNewRequest({ ...newRequest, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="access">Access Instructions</SelectItem>
                  <SelectItem value="schedule">Schedule Preference</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={newRequest.title}
                onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                placeholder="e.g., Please use side door"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                placeholder="Provide details about your request..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
            <Button onClick={submitRequest} disabled={submittingRequest}>
              {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
